import {useMemo} from "react";
import {TaxesData} from "./TaxesListing.types";

import {MRT_ColumnDef} from "material-react-table";
import {CustomSelect} from "ui";
import {Box, Stack, Typography} from "@mui/material";
import pxToRem from "hooks/usePxToRem";
import {Controller, useFormContext, useWatch} from "react-hook-form";

type Props = {
  footer: {valuePaymentTotal: number; valueReceiptTotal: number};
};

const TaxesListingData = (props?: Props) => {
  const {control, getValues} = useFormContext();
  const knowledges = useWatch({control, name: "knowledges"}) ?? [];

  const houseKnowledges = [
    {name: "Nenhum", id: null},
    ...knowledges
      .filter((item) => item.typeKnowledge === "House" && item.isActive)
      .map((item) => ({name: item.number, id: item.id.toString()})),
  ];
  const paymentStyleProp = (isNumeric?: "isNumeric") => ({
    muiTableBodyCellProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.info[200],
        color: theme.palette.grey[700],
        borderRight: `${pxToRem(1)} solid ${theme.palette.grey[300]}`,
        fontSize: pxToRem(12),
        padding: "0.2rem",
        display: "flex",
        justifyContent: isNumeric ? "flex-end" : "center",
      }),
    },
  });

  const receiptStyleProp = (isNumeric?: "isNumeric") => ({
    muiTableBodyCellProps: {
      sx: (theme) => ({
        backgroundColor: theme.palette.success[100],
        color: theme.palette.grey[700],
        borderRight: `${pxToRem(1)} solid ${theme.palette.grey[300]}`,
        fontSize: pxToRem(12),
        padding: "0.2rem",
        display: "flex",
        justifyContent: isNumeric ? "flex-end" : "center",
      }),
    },
  });

  const getChargeIndexById = (id: number) => {
    return (getValues("processCharges") ?? []).findIndex(
      (charge) => charge.id === id,
    );
  };

  const columnsData = useMemo<MRT_ColumnDef<TaxesData>[]>(
    () => [
      {
        id: "generalTaxes",
        header: "Geral",
        columns: [
          {
            accessorKey: "idKnowledgeHouse",
            header: "Conhecimento",
            enableHiding: false,
            size: 130,
            Cell: ({row}) => {
              return (
                <Controller
                  name={`processCharges[${getChargeIndexById(+row.original.id)}].idKnowledgeHouse`}
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <CustomSelect
                      content={houseKnowledges}
                      size="small"
                      value={value?.toString()}
                      onChange={(event) => {
                        onChange(event);
                      }}
                      fullWidth
                      textOnEmptySelect={
                        <Typography sx={{fontSize: pxToRem(12), opacity: 0.5}}>
                          Selecione o House
                        </Typography>
                      }
                      sx={{
                        padding: "0",
                        border: "none",
                        outline: "none",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:focus": {
                          outline: "none",
                        },
                      }}
                      warning={false}
                    />
                  )}
                />
              );
            },
          },
          {
            accessorKey: "rate",
            header: "Taxa",
            enableHiding: false,
          },

          {
            accessorKey: "equipment",
            header: "Equipamento",
          },
          {
            accessorKey: "displayCharge",
            header: "Taxa",
          },
          {
            accessorKey: "originCharge",
            header: "Origem taxa",
          },
          {
            accessorKey: "dateExpiry",
            header: "Validade",
          },
          {
            accessorKey: "typeService",
            header: "Tipo serviço",
            size: 110,
          },
        ],
      },

      {
        id: "pgto",
        header: "Pagamento",
        columns: [
          {
            accessorKey: "modalityPayment",
            header: "Tipo",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "payTo",
            header: "Pagar para",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "paymentPerson",
            id: "paymentPersonName",
            size: 90,
            header: "Pagador",
            ...paymentStyleProp(),
          },
          {
            accessorKey: "paymentCurrency",
            header: "Moeda",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "typePayment",
            header: "Tipo",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "billingPayment",
            header: "Tipo",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "quantityPayment",
            header: "Quant.",
            size: 90,
            ...paymentStyleProp(),
          },
          {
            accessorKey: "valuePaymentUnit",
            header: "Valor",
            size: 90,
            ...paymentStyleProp("isNumeric"),
          },
          {
            accessorKey: "valuePaymentMinimum",
            header: "Valor min.",
            size: 90,
            ...paymentStyleProp("isNumeric"),
          },
          {
            accessorKey: "valuePaymentTotal",
            header: "Valor total",
            size: 90,
            ...paymentStyleProp("isNumeric"),
            aggregationFn: "sum",
            Footer: () =>
              props?.footer?.valuePaymentTotal ? (
                <Stack>
                  Total:
                  <Box>{props?.footer.valuePaymentTotal?.toFixed(2)}</Box>
                </Stack>
              ) : null,
          },
        ],
      },
      {
        id: "receipt",
        header: "Recebimento",
        columns: [
          {
            accessorKey: "receiptCurrency",
            header: "Moeda",
            size: 90,
            ...receiptStyleProp(),
          },
          {
            accessorKey: "valueReceiptUnit",
            header: "Valor",
            size: 90,
            ...receiptStyleProp("isNumeric"),
          },
          {
            accessorKey: "valueReceiptMinimum",
            header: "Valor Mín.",
            size: 90,
            ...receiptStyleProp("isNumeric"),
          },
          {
            accessorKey: "valueReceiptTotal",
            header: "Valor total",
            size: 90,
            ...receiptStyleProp("isNumeric"),
            aggregationFn: "sum",

            Footer: () =>
              props?.footer?.valueReceiptTotal ? (
                <Stack>
                  Total:
                  <Box color="warning.main">
                    {props?.footer.valueReceiptTotal?.toFixed(2)}
                  </Box>
                </Stack>
              ) : null,
          },
          {
            accessorKey: "receiptPerson",
            id: "receiptPersonName",
            header: "Pessoa",
            size: 90,
            ...receiptStyleProp(),
          },
          {
            accessorKey: "typeReceipt",
            header: "Tipo",
            size: 90,
            ...receiptStyleProp(),
          },

          {
            accessorKey: "receiveFrom",
            header: "Receber de",
            size: 90,
            ...receiptStyleProp(),
          },
          {
            accessorKey: "quantityReceipt",
            header: "Quant.",
            size: 90,
            ...receiptStyleProp(),
          },
          {
            accessorKey: "modalityReceipt",
            header: "Modalidade",
            size: 90,
            ...receiptStyleProp(),
          },
        ],
      },
    ],
    [props?.footer, knowledges],
  );

  return {columnsData};
};

export default TaxesListingData;
